import { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth, homeForRole } from "@/context/AuthContext";
import { CtaLink, imgSrc, useContent, useText } from "@/lib/content";

const navClass = ({ isActive }) =>
    `btn-ghost no-underline ${isActive ? "font-bold underline decoration-2 underline-offset-8 decoration-edi-lime" : ""}`;

export const Header = () => {
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const t = useText();
    const { images } = useContent();
    const close = () => setOpen(false);
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        const set = () => document.documentElement.style.setProperty("--edi-header-h", `${el.offsetHeight}px`);
        set();
        const ro = new ResizeObserver(set);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const links = user ? (
        <>
            <li>
                <NavLink to={homeForRole(user.role)} className={navClass} onClick={close} data-testid="nav-home-area-link">
                    {user.role === "azienda" ? "Il mio profilo" : "Area amministrazione"}
                </NavLink>
            </li>
            <li>
                <button type="button" className="btn-tertiary" onClick={() => { close(); logout(); }} data-testid="nav-logout-button">
                    <LogOut aria-hidden="true" size={18} /> Esci
                </button>
            </li>
        </>
    ) : (
        <>
            <li>
                <CtaLink cta={t("home.header_login")} className="px-3 py-2 font-semibold text-edi-text no-underline hover:underline focus-visible:underline underline-offset-4 decoration-2" onClick={close} testId="nav-login-link" />
            </li>
            <li>
                <CtaLink cta={t("home.header_cta")} className="btn-primary" onClick={close} testId="nav-register-link" />
            </li>
        </>
    );

    return (
        <header ref={ref} className="sticky top-0 z-40 bg-edi-surface border-b border-edi-border" data-testid="site-header">
            <div className="mx-auto max-w-6xl px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
                <Link to="/" className="shrink-0" data-testid="header-logo-link">
                    <img src={imgSrc(images.logo.url)} alt={`${images.logo.alt} — vai alla home`} className="edi-logo h-10 sm:h-12 w-auto" width="131" height="48" />
                </Link>
                <nav aria-label="Navigazione principale" className="hidden md:block">
                    <ul className="flex items-center gap-3">{links}</ul>
                </nav>
                <button
                    type="button"
                    className="btn-ghost md:hidden"
                    aria-expanded={open}
                    aria-controls="mobile-menu"
                    onClick={() => setOpen((o) => !o)}
                    data-testid="mobile-menu-button"
                >
                    {open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />} Menu
                </button>
            </div>
            {open && (
                <nav id="mobile-menu" aria-label="Navigazione principale" className="md:hidden border-t border-edi-border px-4 py-3">
                    <ul className="flex flex-col items-stretch gap-2">{links}</ul>
                </nav>
            )}
        </header>
    );
};
